/**
 * businessWorkflow.js — the ONE canonical business-builder workflow
 * definition (Round 218A, 2026-08-02).
 *
 * Dashboard shortcut buttons, onboarding progression, Next Step cards,
 * completion screens, auto-redirects, and locked/unlocked states MUST
 * all consume this file. No component should hardcode "the next page
 * is /X" anymore.
 *
 * Future workflow changes should require editing THIS file only.
 *
 * Shape
 * -----
 * Each step declares:
 *   id             — stable identifier used everywhere
 *   label          — customer-facing title
 *   dashboardCta   — short label used on dashboard shortcut buttons
 *   route          — target route
 *   requires       — array of step ids that must be complete first
 *   isCustomerFacing — true if the step produces a customer-visible
 *                      asset (Sales Page, Lead Magnet, Emails, Ebook,
 *                      Content, Growth). Customer-facing steps CANNOT
 *                      unlock until Brand Builder is complete.
 *   completedWhen  — function(progress) → boolean. Progress is a
 *                    single dict projected from the backend
 *                    (business-brain + brand-brain + offer count +
 *                    intelligence versions + created assets).
 */

export const WORKFLOW = [
  {
    id: 'business_plan',
    label: 'Business Plan',
    dashboardCta: 'Business Plan',
    route: '/business-brain',
    requires: [],
    isCustomerFacing: false,
    completedWhen: (p) => !!p.business_brain_complete,
  },
  {
    id: 'market_intelligence',
    label: 'Market Intelligence',
    dashboardCta: 'Market Intelligence',
    route: '/dashboard#market-intelligence-card',
    requires: ['business_plan'],
    isCustomerFacing: false,
    completedWhen: (p) => !!p.market_intelligence_version,
  },
  {
    id: 'opportunity_intelligence',
    label: 'Opportunity Intelligence',
    dashboardCta: 'Opportunity Intelligence',
    route: '/dashboard#market-intelligence-card',
    requires: ['business_plan'],
    isCustomerFacing: false,
    completedWhen: (p) => !!p.opportunity_intelligence_version,
  },
  {
    id: 'offer',
    label: 'Offer Builder',
    dashboardCta: 'Offer Builder',
    route: '/bots/money',   // Money Bot hosts Build Offer modal today
    requires: ['business_plan'],
    isCustomerFacing: false,
    completedWhen: (p) => (p.offers_created_count || 0) > 0 || !!p.has_offer,
  },
  {
    id: 'brand_builder',
    label: 'Brand Builder',
    dashboardCta: 'Brand Builder',
    route: '/brand-brain-setup',
    requires: ['offer'],
    isCustomerFacing: false,
    completedWhen: (p) => !!p.brand_brain_complete,
  },
  {
    id: 'sales_page',
    label: 'Sales Page',
    dashboardCta: 'Sales Page',
    route: '/sales-page',
    requires: ['brand_builder', 'offer'],
    isCustomerFacing: true,
    completedWhen: (p) => (p.sales_pages_count || 0) > 0,
  },
  {
    id: 'lead_magnet',
    label: 'Lead Magnet',
    dashboardCta: 'Lead Magnet',
    route: '/bots/lead-magnet',
    requires: ['brand_builder', 'offer'],
    isCustomerFacing: true,
    completedWhen: (p) => (p.lead_magnets_count || 0) > 0,
  },
  {
    id: 'emails',
    label: 'Email Campaign',
    dashboardCta: 'Emails',
    route: '/bots/emails',
    requires: ['brand_builder', 'offer'],
    isCustomerFacing: true,
    completedWhen: (p) => (p.emails_count || 0) > 0,
  },
  {
    id: 'essentials',
    label: 'Business Essentials',
    dashboardCta: 'Essentials',
    route: '/bots/essentials',
    requires: ['brand_builder'],
    isCustomerFacing: true,
    completedWhen: (p) => !!p.essentials_created,
  },
  {
    id: 'ebook',
    label: 'Ebook',
    dashboardCta: 'Ebook',
    route: '/bots/ebook',
    requires: ['brand_builder', 'offer'],
    isCustomerFacing: true,
    completedWhen: (p) => (p.ebooks_count || 0) > 0,
  },
  {
    id: 'content',
    label: 'Content',
    dashboardCta: 'Content',
    route: '/bots/content',
    requires: ['brand_builder'],
    isCustomerFacing: true,
    completedWhen: (p) => (p.content_pieces_count || 0) > 0,
  },
  {
    id: 'growth',
    label: 'Growth',
    dashboardCta: 'Growth',
    route: '/bots/growth',
    requires: ['brand_builder', 'sales_page'],
    isCustomerFacing: true,
    completedWhen: (p) => false,   // growth is ongoing
  },
];

/**
 * getStepById(id) — helper for direct lookup.
 */
export const getStepById = (id) => WORKFLOW.find((s) => s.id === id) || null;

/**
 * getNextStep(progress) — return the first step in workflow order
 * whose requirements are satisfied AND whose completedWhen is false.
 * Returns `null` when everything is done (or ongoing).
 */
export const getNextStep = (progress = {}) => {
  const done = new Set();
  for (const step of WORKFLOW) {
    if (step.completedWhen(progress)) done.add(step.id);
  }
  for (const step of WORKFLOW) {
    if (done.has(step.id)) continue;
    const unmet = step.requires.filter((r) => !done.has(r));
    if (unmet.length === 0) return step;
  }
  return null;
};

/**
 * isStepUnlocked(step, progress) — true when every prerequisite is
 * complete AND (for customer-facing steps) Brand Builder is done.
 */
export const isStepUnlocked = (step, progress = {}) => {
  const done = new Set(
    WORKFLOW.filter((s) => s.completedWhen(progress)).map((s) => s.id),
  );
  const requirementsMet = step.requires.every((r) => done.has(r));
  if (!requirementsMet) return false;
  if (step.isCustomerFacing) {
    const brand = getStepById('brand_builder');
    if (brand && !brand.completedWhen(progress)) return false;
  }
  return true;
};

/**
 * projectProgress(user, extras) — normalize the messy set of backend
 * signals we already have (user record + optional counts) into the
 * flat `progress` object the workflow definition expects.
 *
 * This function is where you add new completion signals as the
 * platform grows. Every caller should use `projectProgress` so the
 * shape stays consistent.
 */
export const projectProgress = (user = {}, extras = {}) => ({
  business_brain_complete: !!(user.business_brain_complete ?? user.onboarding_completed),
  brand_brain_complete: !!(user.brand_brain_complete ?? extras.brand_brain_complete),
  market_intelligence_version: extras.market_intelligence_version || null,
  opportunity_intelligence_version: extras.opportunity_intelligence_version || null,
  offers_created_count: user.offers_created_count || 0,
  has_offer: !!(user.has_offer ?? user.active_offer),
  sales_pages_count: extras.sales_pages_count || 0,
  lead_magnets_count: extras.lead_magnets_count || 0,
  emails_count: extras.emails_count || 0,
  essentials_created: !!extras.essentials_created,
  ebooks_count: extras.ebooks_count || 0,
  content_pieces_count: extras.content_pieces_count || 0,
});

export default WORKFLOW;
